import { getPowerSync } from '@/lib/powersync/client'
import { storeBlobForIntent } from '@/lib/db'
import { enqueueIntentAndRun } from '@/lib/intent-worker'
import { compressImageWithThumb } from '@/lib/image-compress'
import { getAccessTokenNoHang } from '@/lib/auth-token'

export type CreateAnalysisMetadata = {
  projectId?: string
  projectName?: string
  locationDetails?: string
  analysisDetails?: string
  inspectionId?: string
  roomId?: string
  roomLabel?: string
  mode?: 'gemini_vision' | 'mock'
  geminiModel?: string
  confidence?: number
  manual?: boolean
  // When true, only the upload-image intent is queued — no run-analysis.
  skipAnalyze?: boolean
  // Pre-compressed payload from `compressImageWithThumb`. Callers that already
  // need the compressed File for UI (e.g. DetectPage's burst preview) should
  // pass this to avoid a second decode pass inside createAnalysis.
  precompressed?: { file: File; thumbDataUrl: string | null }
}

export type CreateAnalysisResult = { mode: 'local'; analysisId: string }

let cachedInternalUserId: string | null = null

// Cleared by AuthProvider on SIGNED_IN/SIGNED_OUT so a session change can't
// leave a stale id behind that subsequent createAnalysis() calls attribute
// new rows to.
export function clearCachedInternalUserIdInCreateAnalysis() {
  cachedInternalUserId = null
}

export async function getInternalUserId(): Promise<string | null> {
  if (cachedInternalUserId) return cachedInternalUserId
  // getAccessTokenNoHang prefers the persisted JWT (no network, never hangs)
  // and only falls back to a timeout-raced getSession() — see auth-token.ts for
  // why awaiting getSession() directly can wedge offline capture forever.
  const accessToken = await getAccessTokenNoHang()
  if (!accessToken) return null
  // Read from the JWT claim the custom_access_token_hook injects, not SQLite:
  // the admin-projection sync bucket delivers admin/superuser rows without
  // `supabaseAuthId`, which clobbers the self-bucket row and breaks a SQLite
  // lookup here.
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1]))
    cachedInternalUserId = typeof payload.internal_user_id === 'string' ? payload.internal_user_id : null
  } catch {
    return null
  }
  return cachedInternalUserId
}

// Local-first "capture → analyze": writes the analysis row + blob locally,
// then enqueues upload + analyze intents. Works online and offline.
export async function createAnalysis(
  blob: Blob,
  metadata: CreateAnalysisMetadata,
): Promise<CreateAnalysisResult> {
  const powersync = getPowerSync()
  if (!powersync) throw new Error('PowerSync not initialized')

  const userId = await getInternalUserId()
  if (!userId) throw new Error('Not signed in')

  // RLS on analyses requires has_project_access("projectId"). A null projectId
  // syncs locally but is 403'd by Supabase on the first push, stranding the
  // row + every dependent intent (upload-image, send-ticket-email) on
  // row_not_synced forever. Fail loudly at the call site instead.
  if (!metadata.projectId) throw new Error('createAnalysis: projectId required')

  const id = crypto.randomUUID()
  const now = new Date().toISOString()

  await powersync.execute(
    `INSERT INTO analyses (
      id, userId, projectId, locationDetails, analysisDetails,
      roomId, pending_inspection_id, pending_room_label, mode, status, isDuplicate,
      offlineQueue, createdAt, updatedAt
    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 0, 0, ?, ?)`,
    [
      id,
      userId,
      metadata.projectId ?? null,
      metadata.locationDetails ?? null,
      metadata.analysisDetails ?? null,
      metadata.roomId ?? null,
      metadata.inspectionId ?? null,
      metadata.roomLabel ?? null,
      metadata.mode ?? 'gemini_vision',
      'pending_upload',
      now,
      now,
    ],
  )

  // Compress at capture so the IDB offlineQueue blob and the wire payload are
  // already small. Without this, iOS can stash a 30MB HEIC per photo. Falls
  // back to the raw blob if decode fails so we still queue something uploadable.
  // Callers can pass `precompressed` to skip a redundant decode (DetectPage's
  // burst/capture paths already need the compressed File for the local preview).
  const compressed = metadata.precompressed
    ?? await compressImageWithThumb(blob)
      .catch(() => ({ file: blob as File, thumbDataUrl: null }))

  await storeBlobForIntent(id, compressed.file)

  if (compressed.thumbDataUrl) {
    await powersync.execute(
      `INSERT INTO analysis_local_thumbs (id, analysis_id, data_url, created_at)
       VALUES (?, ?, ?, ?)`,
      [crypto.randomUUID(), id, compressed.thumbDataUrl, now],
    )
  }

  // Only queue the upload. The server drives Gemini inline after the blob
  // lands (see driveAnalysisInline in /api/uploads/[analysisId]/route.ts),
  // so a separate run-analysis intent would just double the queue depth for
  // no gain. Manual retries on a `failed` row still enqueue run-analysis
  // explicitly from their own call sites.
  await enqueueIntentAndRun('upload-image', { analysisId: id })

  return { mode: 'local', analysisId: id }
}
